/**
 * **A and B, as real controls.** Promoted out of `pages/client-detail/ui/TwoDatesMarkers` on 28
 * September 2026, on its second consumer — the plan overview's „Porównaj tygodnie" hero, whose A
 * and B the owner asked to be the Pomiary tab's own. The rules are `shared/lib/markerSelection`'s.
 *
 * The canon draws two grips a trainer drags. A grip that is only draggable is a control a keyboard
 * cannot reach, which this app does not ship — so each one is an HTML `role="slider"` with its own
 * accessible name, its position in the loaded history as `aria-valuenow`, and its date and reading
 * as `aria-valuetext`. Arrows step to the previous and next MEASUREMENT (never to a pixel, and
 * never past the other marker), Home and End go to the ends, a drag snaps, and a click anywhere on
 * the plot moves whichever marker is nearer.
 *
 * Every one of those gestures reaches the caller as ONE `MarkerMove`, which the caller answers with
 * `shared/lib/markerSelection`'s `moveOnTimes` — the rules that make a move legal are stated once.
 *
 * ── It does not fight the chart's own readout ────────────────────────────────────────────────
 * `ReadoutChart`'s plot is itself a tab stop with its own arrow handling. These handles stop
 * propagation on their key and pointer events and nothing here is bound to the plot's tab stop, so
 * the crosshair keeps walking the readings and the markers keep walking the selection. The click
 * that moves the nearer marker is a native listener on the chart's own box rather than an HTML
 * layer over it: a layer with `pointer-events: auto` above the svg would eat every mousemove and
 * kill the hover readout silently. That listener REFUSES a press that landed on a handle itself —
 * see it below, and note that a synthetic `stopPropagation` on the handle cannot do it.
 *
 * A chart CHILD, like `TwoDatesOverlay`, because the handles are positioned from `xScale`. They are
 * HTML rather than SVG, so `role="slider"` and the keyboard reach them the way they reach any other
 * control on the screen.
 */

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { SERIES_DATE_KEY, SERIES_VALUE_KEY } from '~/shared/lib/chartSeries';
import type { MarkerKey, MarkerMove, SelectionMarkerView } from '~/shared/lib/markerSelection';

import { useChartStable } from './chartOverlay';
import styles from './SelectionMarkers.module.css';

/**
 * How far a handle reaches above the plot: the chip's own 20px plus the 2px it stands clear by.
 *
 * A number rather than a token because it is read in a `style` object that positions the handle
 * inside the chart's own pixel space; the stylesheet's `--h-two-dates-grip` and
 * `--h-two-dates-grip-gap` are the same two values, and the chip is SIZED from those.
 */
const CHIP_BLOCK = 22;

export interface SelectionMarkersProps {
  readonly markers: readonly [SelectionMarkerView, SelectionMarkerView];
  /** The accessible name of each handle — "Data A", "Data B". */
  readonly nameOf: (key: MarkerKey) => string;
  readonly onMove: (move: MarkerMove) => void;
}

export function SelectionMarkers({ markers, nameOf, onMove }: SelectionMarkersProps) {
  const { xScale, yScale, margin, innerWidth, innerHeight, containerRef, data } = useChartStable();
  /**
   * The plot's own box, taken in an EFFECT rather than read out of the ref during render — this
   * app's lint refuses the second and the React Compiler cannot see through it.
   */
  const [box, setBox] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setBox(containerRef.current);
  }, [containerRef]);

  /**
   * A click anywhere on the PLOT moves the nearer marker. Native, so hover is untouched.
   *
   * ── It has to refuse the handles itself ──────────────────────────────────────────────────────
   * The handles' React `onClick` calls `stopPropagation`, and that is not enough: React's synthetic
   * events are delivered from a listener on the root, which runs AFTER a native listener on an
   * ancestor inside the same container. So a press on the A handle reached this listener first and
   * moved whichever marker happened to be nearer — usually the other one. The guard is on the
   * TARGET rather than on a flag, because "the press started on a handle" is a fact about the DOM
   * and the handle is the thing that knows it.
   *
   * ── And the x-axis strip is not the plot ─────────────────────────────────────────────────────
   * `y` was unguarded, so a click on the date labels under the plot — outside `innerHeight`, on the
   * same box — moved a marker to whatever instant that x happened to name.
   */
  useEffect(() => {
    if (box === null) return undefined;
    const onClick = (event: MouseEvent) => {
      const target = event.target;
      if (target instanceof Element && target.closest(`.${String(styles.marker)}, [data-plot-control]`) !== null) return;
      const bounds = box.getBoundingClientRect();
      const x = event.clientX - bounds.left - margin.left;
      const y = event.clientY - bounds.top - margin.top;
      if (x < 0 || x > innerWidth || y < 0 || y > innerHeight) return;
      onMove({ kind: 'pick', at: xScale.invert(x).getTime() });
    };
    box.addEventListener('click', onClick);
    return () => {
      box.removeEventListener('click', onClick);
    };
  }, [box, margin.left, margin.top, innerWidth, innerHeight, onMove, xScale]);

  if (box === null || innerWidth <= 0) return null;

  const valueAt = (instant: number): number | null => {
    const row = data.find((entry) => {
      const date = entry[SERIES_DATE_KEY];
      return date instanceof Date && date.getTime() === instant;
    });
    const value = row?.[SERIES_VALUE_KEY];
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
  };

  const instantFrom = (clientX: number): number => {
    const bounds = box.getBoundingClientRect();
    return xScale.invert(clientX - bounds.left - margin.left).getTime();
  };

  return createPortal(
    <div
      className={styles.markerLayer}
      /**
       * **THE HANDLES' MOUSE EVENTS MUST NOT REACH THE CHART'S INTERACTION LAYER.**
       *
       * These handles are portalled into the chart's own box, but in the REACT tree they are
       * children of the chart — so every event raised on a handle propagates to bklit's interaction
       * `<g>` however far apart the two are in the DOM. During a drag the pointer is captured by
       * the handle, so bklit received a `mousemove` whose target was the handle, and `localPoint`
       * resolved the coordinate against the HANDLE's 24px box rather than against the plot's svg:
       * a chartX of about −37, which bisects to the FIRST reading. Worse, no `mouseleave` ever
       * reached the `<g>` — the pointer never left it in React's tree — so bklit's own re-anchor
       * effect kept re-resolving that same −37 on every later re-render and the readout stayed
       * pinned to the first point long after the drag ended. That is the defect the owner saw.
       *
       * Stopped on the LAYER rather than on each handle: the layer is the React parent of both, so
       * the rule is stated once and a third handle cannot forget it. The chart therefore keeps
       * whatever the pointer last told it while a handle is being dragged, and resumes normal
       * hover the moment the pointer is back over the plot.
       */
      onMouseDown={(event) => {
        event.stopPropagation();
      }}
      onMouseEnter={(event) => {
        event.stopPropagation();
      }}
      onMouseLeave={(event) => {
        event.stopPropagation();
      }}
      onMouseMove={(event) => {
        event.stopPropagation();
      }}
      onMouseUp={(event) => {
        event.stopPropagation();
      }}
    >
      {markers.map((marker) => {
        const instant = marker.at.getTime();
        const value = valueAt(instant);
        return (
          <div
            aria-label={nameOf(marker.key)}
            aria-valuemax={marker.max}
            aria-valuemin={marker.min}
            aria-valuenow={marker.index}
            aria-valuetext={marker.valueText}
            className={styles.marker}
            key={marker.key}
            onKeyDown={(event) => {
              if (event.key === 'ArrowRight') onMove({ kind: 'step', marker: marker.key, by: 1 });
              else if (event.key === 'ArrowLeft')
                onMove({ kind: 'step', marker: marker.key, by: -1 });
              else if (event.key === 'Home')
                onMove({ kind: 'end', marker: marker.key, to: 'first' });
              else if (event.key === 'End') onMove({ kind: 'end', marker: marker.key, to: 'last' });
              else return;
              // The plot behind these handles walks its own readout with the same four keys.
              event.preventDefault();
              event.stopPropagation();
            }}
            onPointerDown={(event) => {
              event.currentTarget.setPointerCapture(event.pointerId);
              event.stopPropagation();
            }}
            onPointerMove={(event) => {
              if (!event.currentTarget.hasPointerCapture(event.pointerId)) return;
              onMove({ kind: 'drag', marker: marker.key, at: instantFrom(event.clientX) });
            }}
            onPointerUp={(event) => {
              event.currentTarget.releasePointerCapture(event.pointerId);
            }}
            role="slider"
            style={{
              insetInlineStart: `${String(margin.left + (xScale(marker.at) ?? 0))}px`,
              // The chip stands ABOVE the plot, clear of the box, and the stem runs from its foot
              // to the plot's floor — which is the canon's own picture and what the 30px of top
              // margin in `BodyTab`'s `PLOT_MARGIN` was reserved for. Drawn from the plot's top
              // edge the chip covered the first fifth of the line.
              insetBlockStart: `${String(margin.top - CHIP_BLOCK)}px`,
              blockSize: `${String(innerHeight + CHIP_BLOCK)}px`,
            }}
            tabIndex={0}
          >
            <span aria-hidden="true" className={styles.markerChip}>
              {marker.key === 'a' ? 'A' : 'B'}
            </span>
            <span aria-hidden="true" className={styles.markerStem} />
            {value === null ? null : (
              <span
                aria-hidden="true"
                className={styles.markerDot}
                // Measured from the handle's own top, which begins `CHIP_BLOCK` above the plot.
                style={{ insetBlockStart: `${String(CHIP_BLOCK + (yScale(value) ?? 0))}px` }}
              />
            )}
          </div>
        );
      })}
    </div>,
    box,
  );
}

/** Above the series and outside the reveal clip — see `ReadoutChart`'s `children`. */
SelectionMarkers.displayName = 'SelectionMarkers';
(SelectionMarkers as { __isPostOverlay?: boolean }).__isPostOverlay = true;
